import { FC, useEffect, useState } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

import { Colors } from "../../../configs";
import { CustomButton, CustomTextBox } from "../../atoms";
import { CustomDrawer } from "../../atoms/custom-drawer/custom-drawer";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import DiscardConfirmationModal from "../../common/discard-confirmation-modal/DiscardConfirmationModal";
import {
    createRedoReasonAction,
    updateRedoReasonAction,
    getAllRedoReasonsAction,
} from "../../../redux/actions/redoReasonsAction";
import { useSnackbarContext } from "../../../providers/SnackbarProvider";

const schema = Yup.object().shape({
    reason: Yup.string().required("Reason description is required"),
});

type RedoReasonFormData = Yup.InferType<typeof schema>;

export interface ICreateRedoReasonDrawerProps {
    open: boolean;
    toggleDrawer: () => void;
    populateFor?: { id?: number; reason: string };
}

export const AddRedoReasonDrawer: FC<ICreateRedoReasonDrawerProps> = ({
    open,
    toggleDrawer,
    populateFor,
}) => {
    const dispatch = useAppDispatch();
    const [exitConfirmOpen, setExitConfirmOpen] = useState(false);
    const {
        addRedoReasonLoading,
        updateRedoReasonLoading,
        deleteRedoReasonLoading,
        redoReasonsLoading,
    } = useAppSelector((state) => state.redoReason);

    const { control, handleSubmit, reset, formState: { isDirty } } = useForm<RedoReasonFormData>({
        resolver: yupResolver(schema),
        defaultValues: {
            reason: "",
        },
    });
    const snackBar = useSnackbarContext();

    useEffect(() => {
        if (open) {
            reset({ reason: populateFor?.reason || "" });
        }
    }, [open, populateFor, reset]);

    const handleSaveReason = async (data: RedoReasonFormData) => {
        const payload = {
            storeId: 1,
            merchantId: 1,
            reason: data.reason,
        };

        try {
            if (populateFor?.id) {
                await dispatch(
                    updateRedoReasonAction({ ...payload, id: populateFor.id })
                ).unwrap();
                snackBar.showSnackbar("Redo Reason updated successfully!", "success");
            } else {
                await dispatch(createRedoReasonAction(payload)).unwrap();
                snackBar.showSnackbar("Redo Reason added successfully!", "success");
            }
            dispatch(getAllRedoReasonsAction());
            toggleDrawer();
        } catch (error) {
            snackBar.showSnackbar(
                "Failed to save redo reason. Please try again.",
                "error"
            );
        }
    };

    const handleCloseAttempt = () => {
        if (isDirty) {
            setExitConfirmOpen(true);
        } else {
            toggleDrawer();
        }
    };

    return (
        <>
        <DiscardConfirmationModal
            open={exitConfirmOpen}
            onKeepEditing={() => setExitConfirmOpen(false)}
            onDiscard={() => { reset(); setExitConfirmOpen(false); toggleDrawer(); }}
        />
        <CustomDrawer
            isOpen={open}
            toggleDrawer={handleCloseAttempt}
            sx={{ width: "28.65rem" }}
        >
            <form onSubmit={handleSubmit(handleSaveReason)}>
                <Box
                    sx={{
                        paddingInline: "1.857rem",
                        paddingTop: "2rem",
                        display: "flex",
                        flexDirection: "column",
                        gap: "2.5rem",
                    }}
                >
                    <Typography
                        sx={{
                            fontWeight: "bold",
                            fontSize: "1.4rem",
                            lineHeight: "1.7rem",
                            color: Colors.TEXT_GRAY_DARK,
                        }}
                    >
                        {populateFor ? "Update Redo Reason" : "Add a New Redo Reason"}
                    </Typography>

                    <Box sx={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}>
                        <Typography>Reason Description</Typography>
                        <Controller
                            name="reason"
                            control={control}
                            render={({ field, fieldState }) => (
                                <CustomTextBox
                                    {...field}
                                    error={fieldState.invalid}
                                    helperText={fieldState.error?.message}
                                    sx={{ width: "100%", backgroundColor: Colors.WHITE }}
                                    placeholder="Enter reason details"
                                />
                            )}
                        />
                    </Box>

                    <CustomButton
                        sx={{ width: "12rem", height: "2.6rem", marginTop: "1rem" }}
                        type="submit"
                        disabled={
                            addRedoReasonLoading ||
                            updateRedoReasonLoading ||
                            deleteRedoReasonLoading ||
                            redoReasonsLoading
                        }
                    >
                        {addRedoReasonLoading ||
                            updateRedoReasonLoading ||
                            deleteRedoReasonLoading ||
                            redoReasonsLoading ? (
                            <CircularProgress size={20} color="inherit" />
                        ) : populateFor ? (
                            "Update Reason"
                        ) : (
                            "Add Reason"
                        )}
                    </CustomButton>
                </Box>
            </form>
        </CustomDrawer>
        </>
    );
};
